import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Plus,
  Layers,
  Gamepad2,
  GripVertical,
  Edit,
  Eye,
  RefreshCcw,
  Loader2,
  AlertCircle,
  Search,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { GameService, type GameCategory as APICategory } from '@/domain/game'

// Extended Category interface for UI
interface Category extends APICategory {
  // UI-specific fields
  slug?: string
  description?: string
  totalBets?: number
  totalPlayers?: number
  isFeatured?: boolean
  isActive?: boolean
}

export function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Load categories from API
  const loadCategories = async () => {
    try {
      setError(null)
      const result = await GameService.getCategoriesFromGames()
      
      // Map API data to UI format with defaults
      const mappedCategories: Category[] = result.items.map((c) => ({
        ...c,
        slug: c.name?.toLowerCase()?.replace(/\s+/g, '-') || '',
        description: `เกม${c.nameTh}ทุกค่าย`,
        totalBets: 0,
        totalPlayers: 0,
        isFeatured: (c.order || 99) <= 3,
        isActive: c.status === 'ACTIVE',
      }))
      
      setCategories(mappedCategories)
    } catch (err: any) {
      console.error('[CategoriesPage] Error loading categories:', err)
      setError(err.message || 'ไม่สามารถโหลดข้อมูลหมวดหมู่ได้')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  // Initial load
  useEffect(() => {
    loadCategories()
  }, [])

  // Refresh handler
  const handleRefresh = () => {
    setIsRefreshing(true)
    loadCategories()
  }

  // Filter categories
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categories
    const term = searchTerm.toLowerCase()
    return categories.filter(c =>
      c.name?.toLowerCase().includes(term) ||
      c.nameTh?.toLowerCase().includes(term)
    )
  }, [searchTerm, categories])

  const formatNumber = (num: number) => new Intl.NumberFormat('th-TH').format(num)

  const totalStats = useMemo(() => ({
    categories: categories.length,
    games: categories.reduce((sum, c) => sum + (c.gamesCount || 0), 0),
    activeGames: categories.reduce((sum, c) => sum + (c.activeGames || 0), 0),
  }), [categories])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">กำลังโหลดข้อมูลหมวดหมู่...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
          <p className="mt-2 text-lg font-medium">เกิดข้อผิดพลาด</p>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={handleRefresh} className="mt-4">
            <RefreshCcw className="h-4 w-4 mr-2" />
            ลองใหม่
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">หมวดหมู่เกม</h1>
          <p className="text-muted-foreground">จัดการหมวดหมู่และการจัดเรียงเกม</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCcw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
            รีเฟรช
          </Button>
          <Button className="gap-2" disabled>
            <Plus className="h-4 w-4" />
            เพิ่มหมวดหมู่
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Layers className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">หมวดหมู่ทั้งหมด</p>
                <p className="text-xl font-bold">{totalStats.categories}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <Gamepad2 className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">เกมทั้งหมด</p>
                <p className="text-xl font-bold">{formatNumber(totalStats.games)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <Gamepad2 className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">เกมเปิดใช้งาน</p>
                <p className="text-xl font-bold text-win">{formatNumber(totalStats.activeGames)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <Layers className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">สถานะ</p>
                <p className="text-xl font-bold text-win">เชื่อมต่อ API</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="ค้นหาหมวดหมู่..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* No data */}
      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <Layers className="h-12 w-12 mx-auto text-muted-foreground" />
          <p className="mt-2 text-lg font-medium">ไม่พบหมวดหมู่</p>
          <p className="text-muted-foreground">
            {searchTerm ? 'ลองค้นหาด้วยคำอื่น' : 'กรุณา Sync ข้อมูลเกมก่อน'}
          </p>
        </div>
      )}

      {/* Categories List */}
      {filteredCategories.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              รายการหมวดหมู่
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-y">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground w-10"></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">หมวดหมู่</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">เกม</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">เปิดใช้งาน</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">Featured</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">สถานะ</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.sort((a, b) => (a.order || 99) - (b.order || 99)).map((category) => (
                    <tr key={category.id} className={cn("border-b hover:bg-muted/30", !category.isActive && "opacity-60")}>
                      <td className="px-4 py-3">
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-xl">
                            {category.icon || '🎮'}
                          </div>
                          <div>
                            <p className="font-medium">{category.nameTh || category.name}</p>
                            <p className="text-xs text-muted-foreground">{category.name} • /{category.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="secondary" className="bg-muted">
                          {category.gamesCount || 0}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="secondary" className="bg-win/10 text-win">
                          {category.activeGames || 0}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {category.isFeatured ? (
                          <Badge className="bg-yellow-500 text-white border-0">⭐ Featured</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch checked={category.isActive !== false} disabled />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => setSelectedCategory(category)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Detail Dialog */}
      <Dialog open={!!selectedCategory} onOpenChange={() => setSelectedCategory(null)}>
        <DialogContent className="max-w-md">
          {selectedCategory && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center text-2xl">
                    {selectedCategory.icon || '🎮'}
                  </div>
                  <div>
                    <p>{selectedCategory.nameTh || selectedCategory.name}</p>
                    <p className="text-sm font-normal text-muted-foreground">{selectedCategory.name}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">{selectedCategory.description}</p>

                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{selectedCategory.gamesCount || 0}</p>
                    <p className="text-xs text-muted-foreground">เกมทั้งหมด</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold text-win">{selectedCategory.activeGames || 0}</p>
                    <p className="text-xs text-muted-foreground">เปิดใช้งาน</p>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Slug</span>
                    <code className="text-xs bg-muted px-2 py-0.5 rounded">/{selectedCategory.slug}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ลำดับ</span>
                    <span>{selectedCategory.order || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Featured</span>
                    <span>{selectedCategory.isFeatured ? '⭐ ใช่' : 'ไม่'}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-sm text-muted-foreground">สถานะ</span>
                  <Switch checked={selectedCategory.isActive !== false} disabled />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
